<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';
import LoadingIcon from './LoadingIcon.vue';
import { useSessionStore } from '../../stores';

interface Props {
    src: string
    orientation?: 'horizontal' | 'vertical' | 'full'
    allow?: string
}
const { src, orientation = 'horizontal', allow } = defineProps<Props>()

const sessionStore = useSessionStore()

const isLoading = ref<boolean>(true)
const wrapper = ref<HTMLElement | null>(null)
const parent = computed<HTMLElement | null>(() => wrapper.value?.parentElement || null)
const srcNoCache = computed<string>(() => {
    const joiner = src.includes('?') ? '&' : '?'
    const newSrc = [src, `nocache=${Date.now()}`].join(joiner)
    return newSrc
})

const { width: parentWidth, height: parentHeight } = useElementSize(parent)

const wrapperClass = computed<string>(() => {
    if (orientation === 'full') return 'size-full';

    const aspectClass = orientation === 'horizontal' ? 'aspect-video' : 'aspect-[12/16]'
    const baseRatio = orientation === 'horizontal' ? 16 / 9 : 12 / 16
    const parentRatio = parentWidth.value / parentHeight.value

    if (parentRatio > baseRatio) return `${aspectClass} h-full`;
    return `${aspectClass} w-full`
})
</script>

<template>
    <div ref="wrapper" class="relative bg-white rounded-lg overflow-hidden" :class="[wrapperClass, {
        '!size-full': sessionStore.isFullscreen
    }]">
        <div v-if="isLoading" class="h-full flex items-center justify-center">
            <LoadingIcon />
        </div>
        <iframe v-show="!isLoading" :src="srcNoCache" class="size-full" :allow="allow"
            @load="isLoading = false"></iframe>
    </div>
</template>